import React from 'react';
import { configData } from "../config.js";
import AgreementSelection from '../components/useCase/AgreementSelection.js';
import '../styles.scss';
import { fetchSigned } from "../shared/Utilities.js"
import DelayedTooltip from "../components/general/DelayedTooltip.js";
import CustomerAgreementDetails from "../components/agreement/CustomerAgreementDetails.js";

import {
  OwcButton,
  OwcTypography,
  OwcExpandable,
  OwcTable,
  OwcTableHeader,
  OwcTableHeaderCell,
  OwcTableBody,
  OwcTableCell,
  OwcTableGroup,
  OwcTableRow,
   OwcIconButton,
  OwcIcon
} from '@one/react';

/**
 * The mainly read only form with a single verify button for showing the account and device mappings for an agreement
 *
 * @copyright Roche 2022
 * @author Nick Draper
 */

class identifyConflicts extends React.Component {
  /**
   * Constructor
   *
   * @param props The properties passed
   */
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      agreement: {},
      agreementList: [],
      selectedAffiliate: 0,
      publishedOnly: "All",
      affiliateList: [],
      resetAgreementControl: Math.random(),
      conflictingAgreements: [],
      conflictingDevices: [],
      conflictingAgreementsLoaded: false,
      conflictingDevicesLoaded: false,
      conflictingDevicesLoading: false,
      conflictingDevicesSaving: false,
      conflictingDevicesSaved: false,
      conflictingAgreementsExpanded: false,
      resultExpanded: false,
      isCustomerAgreementExpanded: true,
      refListsComplete: [],
      refdataLoaded: false,
      selectedAgreementLoading: true,
    };
  }

  componentDidMount() {
    // Existing affiliate list call
    fetchSigned(configData.REFDATA_API_URL + "?includeInactive=false")
      .then(res => res.json())
      .then(
        (result) => {
          const filteredAffiliateList = result.filter(
            value => value.type === "Affiliate"
          );
          this.setState({ affiliateList: filteredAffiliateList,
                          refListsComplete: result,
                          refdataLoaded: true
           });
        },
        (error) => { this.setState({ error }) }
      );

    // Load conflicting agreements and base agreement
    this.loadConflictingAgreements();
    this.loadBaseAgreement();
  }

  /** Runs whenever the properties of the control are changed
   * @param prevProps The previous properties dictionary
   * @param prevState The previous state dictionary
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.customerAgreementId !== this.props.customerAgreementId) {
      this.loadConflictingAgreements();
      this.loadBaseAgreement();
    }
  }

  /**
   * Checks if the agreement list has been modified from the saved state
   */
  hasUnsavedChanges() {
    const { agreementList, conflictingAgreements } = this.state;
    if (agreementList.length !== conflictingAgreements.length) return true;
    const savedIds = conflictingAgreements.map(a => a.customerAgreementId).sort();
    const currentIds = agreementList.map(a => a.customerAgreementId).sort();
    return JSON.stringify(savedIds) !== JSON.stringify(currentIds);
  }

  /**
   * Resets the agreement list to the last saved state and clears results
   */
  clearUnsavedChanges() {
    this.setState({
      agreementList: [...this.state.conflictingAgreements],
      conflictingDevices: [],
      conflictingDevicesLoaded: false,
    });
  }

  /**
   * Loads the base agreement details for CSV export
   */
  loadBaseAgreement() {
    const agreementId = this.props.customerAgreementId;
    if (!agreementId) return;

    fetchSigned(configData.CONTRACTS_API_URL + agreementId + "/")
      .then(res => res.json())
      .then(
        (result) => {
          this.setState({ agreement: result[0],
            selectedAgreementLoading: false
           });
        },
        (error) => { this.setState({ error }) }
      );
  }

  // Download CSV util
  downloadConflictsCSV() {
    const { agreement, conflictingDevices, affiliateList } = this.state;

    // Resolve base agreement affiliate name from affiliateList
    const baseAffiliate = affiliateList.find(a => a.refListId === agreement.rocheAffiliateId);
    const baseAffiliateName = baseAffiliate ? baseAffiliate.description : '';

    const rows = [];

    // Base Agreement details
    rows.push('"Base Agreement details:"');
    rows.push('"Agreement Id","Affiliate","Agreement Name","Customer Name"');
    rows.push([
      agreement.customerAgreementId,
      '"' + baseAffiliateName + '"',
      '"' + (agreement.title || '') + '"',
      '"' + (agreement.customerName || '') + '"',
    ].join(','));
    rows.push('');

    // Conflicting Agreements
    rows.push('"Conflicting Agreements:"');

    conflictingDevices.forEach((ca) => {
      rows.push('"Agreement Id","Affiliate","Agreement Name","Customer Name"');
      rows.push([
        ca.agreementId,
        '"' + (ca.affiliateName || '') + '"',
        '"' + (ca.agreementTitle || '') + '"',
        '"' + (ca.customerName || '') + '"',
      ].join(','));

      rows.push(',"Devices:"');
      rows.push(',"Ref System Type","Serial No","Device Id","Ship To Account No","Sold To Account No","Bill To Account No","Location Account No","Location Account Name","Location Account Address","Conflict Period"');

      if (ca.conflicts) {
        ca.conflicts.forEach((c) => {
          rows.push([
            '',
            '"' + (c.referenceSystemType || '') + '"',
            '"' + (c.serialNo || '') + '"',
            c.deviceId || '',
            '"' + (c.shipToAccountNo || '') + '"',
            '"' + (c.soldToAccountNo || '') + '"',
            '"' + (c.billToAccountNo || '') + '"',
            '"' + (c.instrumentLocationAccountNo || '') + '"',
            '"' + (c.locationAccountName || '') + '"',
            '"' + (c.instrumentAddress || '') + '"',
            '"' + (c.conflictStartDate || '') + ' to ' + (c.conflictEndDate || '') + '"',
          ].join(','));
        });
      }

      rows.push('');
    });

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'identify_conflicts_' + this.props.customerAgreementId + '.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  // GET api for conflicting agreements
  loadConflictingAgreements() {
    const agreementId = this.props.customerAgreementId;

    if (!agreementId) return;

    this.setState({ conflictingAgreementsLoaded: false });

    fetchSigned(configData.IDENTITY_CONFLICTS_API_URL + "get-conflicting-agreements/" + agreementId)
      .then(res => res.json())
      .then(
  (result) => {
    // Normalize missing fields so AgreementListItem icon checks work correctly
    const normalized = result.map(a => ({
      ...a,
      publishedVersionNo: a.publishedVersionNo ?? null,
      deviceVerifiedDate: a.deviceVerifiedDate ?? null,
    }));
    this.setState({
      conflictingAgreements: normalized,
      agreementList: normalized,
      conflictingAgreementsLoaded: true,
      conflictingAgreementsExpanded: this.state.conflictingAgreementsExpanded || result.length === 0
    }, ()=>{if (result.length !== 0) {
        this.searchConflicts();
      }
    });
  },
  (error) => { this.setState({ error }) }
  );
  }

  // POST api to save conflicting agreements
  saveConflicts() {
    const agreementId = this.props.customerAgreementId;
    const conflictingAgreementList = this.state.agreementList.map(
      a => a.customerAgreementId
    );

    this.setState({ conflictingDevicesSaving: true, conflictingDevicesSaved: false });

    fetchSigned(configData.IDENTITY_CONFLICTS_API_URL + "save-conflicting-agreements/" + agreementId, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        conflictingAgreementList: conflictingAgreementList,
      })
    })
      .then(res => res.json())
      
      .then(
        (result) => {
          this.setState({
            conflictingDevices: [],
            conflictingDevicesSaved: true,
            conflictingDevicesSaving: false,
            conflictingDevicesLoaded: false, 
            conflictingAgreements: [...this.state.agreementList],
          });
        },
        (error) => {
          this.setState({ error, conflictingDevicesSaving: false });
        }
    );
  }

  // POST api to search conflicts
  searchConflicts() {
    const agreementId = this.props.customerAgreementId;

    this.setState({ conflictingDevicesLoading: true, conflictingDevicesLoaded: false, conflictingDevices: [] });

    fetchSigned(configData.IDENTITY_CONFLICTS_API_URL + "search-conflicts/" + agreementId, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      
      .then(
        (result) => {
          // Group flat response by agreementId so duplicate entries are merged
          const grouped = [];
          const agreementMap = {};
          result.forEach((item) => {
            if (agreementMap[item.agreementId]) {
              agreementMap[item.agreementId].conflicts.push(...(item.conflicts || []));
            } else {
              const entry = { ...item, conflicts: [...(item.conflicts || [])] };
              agreementMap[item.agreementId] = entry;
              grouped.push(entry);
            }
          });
          this.setState({
            conflictingDevices: grouped,
            conflictingDevicesLoaded: true,
            conflictingDevicesLoading: false,
            conflictingAgreements: [...this.state.agreementList],
            resultExpanded: true
          });
        },
        (error) => {
          this.setState({ error, conflictingDevicesLoading: false });
        }
      );
  }

  renderAgreementFilterSection() {
    const { agreementList, selectedAffiliate, publishedOnly, affiliateList } = this.state;

    const saveDisabled = (!this.hasUnsavedChanges()) || this.state.conflictingDevicesSaving;
    const searchDisabled = this.hasUnsavedChanges() || this.state.conflictingDevicesSaving
                                                    || this.state.conflictingDevicesLoading 
                                                    || this.state.conflictingAgreements.length === 0;

    return (
      <div>
        <div style={{ padding: '0.1rem 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.3rem' }}>
            <div style={{ width: '200px' }}>
              <OwcTypography style={{ fontWeight: '500' }}>
                Filter by affiliate
              </OwcTypography>
            </div>

            <select
              value={selectedAffiliate}
              onChange={(ev) =>
                this.setState({ selectedAffiliate: ev.target.value })
              }
              style={{ height: '3em', minWidth: '250px' }}
            >
              <option value="0">All</option>
              {affiliateList.map((affiliate) => (
                <option key={affiliate.refListId} value={affiliate.refListId}>
                  {affiliate.description}
                </option>
              ))}
            </select>
          </div>

          {/* Agreement Selection */}
          <AgreementSelection
            selectedList={agreementList}
            affiliateFilter={selectedAffiliate}
            publishedFilter={publishedOnly}
            searchOnStart={true}
            resetSearch={this.state.resetAgreementControl}
            onRemoveSelectedItem={(agreementToRemove) => {
              const newAgreementList = agreementList.filter(
                agreement =>
                  agreement.customerAgreementId !==
                  agreementToRemove.customerAgreementId
              );
              this.setState({ agreementList: newAgreementList });
            }}
            onAddSelectedItem={(agreement) => {
              this.setState({
                agreementList: [...agreementList, agreement],
              });
            }}
          />
        </div>
        {this.hasUnsavedChanges() && (
          <OwcTypography style={{ color: 'orange', paddingTop: '0.5rem' }}>
            You have unsaved changes to the selected agreement list.
          </OwcTypography>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '1rem' }}>
          <OwcButton
            onClick={() => this.clearUnsavedChanges()}
            disabled={!this.hasUnsavedChanges()}
          >
            Clear Unsaved Changes
          </OwcButton>
          <OwcButton
            onClick={() => this.saveConflicts()}
            disabled={saveDisabled}
          >
            {this.state.conflictingDevicesSaving ? 'Saving...' : 'Save Conflicting Agreements'}
          </OwcButton>
          <OwcButton
            onClick={() => this.searchConflicts()}
            disabled={searchDisabled}
          >
            {this.state.conflictingDevicesLoading ? 'Searching...' : 'Find Conflicting Devices'}
          </OwcButton>
        </div>
      </div>
    );
  }

  renderConflictingDevicesTable() {
    const { conflictingDevices } = this.state;

    return (
      <div>
        <OwcTable
          elevated
          vertical-border
          show-group-expander-column
          sticky-column-end="1"
          style={{ maxHeight: 'calc(100vh - 48px)' }}
        >
          <OwcTableHeader sticky>
            <OwcTableHeaderCell align="left" resizable>
              Ref System Type
            </OwcTableHeaderCell>
            <OwcTableHeaderCell align="left" resizable>
              Serial No
            </OwcTableHeaderCell>
            <OwcTableHeaderCell align="left" resizable>
              Location Account No
            </OwcTableHeaderCell>
            <OwcTableHeaderCell align="left" resizable>
              Location Account Name
            </OwcTableHeaderCell>
            <OwcTableHeaderCell align="left" resizable>
              Location Account Address
            </OwcTableHeaderCell>
            <OwcTableHeaderCell align="left" resizable>
              Conflict Period
            </OwcTableHeaderCell>
          </OwcTableHeader>
          <OwcTableBody>
            {conflictingDevices.map((agreement) => (
              <OwcTableGroup key={agreement.agreementId} expandable>
                 <span slot="header">
                  <span style={{display:'flex',alignItems:'center'}} slot='suffix'>
                  Agreement {agreement.agreementId} - {agreement.agreementTitle}
                 
                  <OwcIconButton
                    id={"conflictAgreementLookup" + agreement.agreementId}
                    onClick={(e) => { e.stopPropagation(); this.props.onAgreementClick(agreement.agreementId); }}
                  >
                    <OwcIcon type="outlined" name="search" style={{fontSize: "0.75em"}} />
                  </OwcIconButton>
                  <DelayedTooltip anchor={"conflictAgreementLookup" + agreement.agreementId}>
                    Go to the map devices screen for this agreement
                  </DelayedTooltip>
                 </span>
                </span>
                
                {agreement.conflicts && agreement.conflicts.map((conflict, index) => (
                  <OwcTableRow key={agreement.agreementId + '-' + index}>
                    <OwcTableCell>{conflict.referenceSystemType}</OwcTableCell>
                    <OwcTableCell>{conflict.serialNo}</OwcTableCell>
                     <OwcTableCell>{conflict.instrumentLocationAccountNo}</OwcTableCell>
                    <OwcTableCell>{conflict.locationAccountName}</OwcTableCell>
                    <OwcTableCell>{conflict.instrumentAddress}</OwcTableCell>
                    <OwcTableCell>{conflict.conflictStartDate} to {conflict.conflictEndDate}</OwcTableCell>
                  </OwcTableRow>
                ))}
              </OwcTableGroup>
            ))}
          </OwcTableBody>
        </OwcTable>
      </div>
    );
  }

  render() {
    const totalConflicts = this.state.conflictingDevices.reduce(
      (sum, a) => sum + (a.conflicts ? a.conflicts.length : 0), 0
    );

    const uniqueDeviceIds = new Set();
    this.state.conflictingDevices.forEach(a => {
      if (a.conflicts) {
        a.conflicts.forEach(c => {
          if (c.deviceId != null) uniqueDeviceIds.add(c.deviceId);
        });
      }
    });
    const uniqueDeviceCount = uniqueDeviceIds.size;

    if (this.state.selectedAgreementLoading === true || this.state.refdataLoaded === false) {
      return (<OwcTypography variant="title6" style={{marginLeft:"0.5em"}}>Loading ...</OwcTypography>);
    }

    return (
      <div style={{ marginLeft: "0.5em", width: "99%" }}>
        <OwcTypography variant="title5">Agreement-{this.props.customerAgreementId}</OwcTypography>
        <OwcExpandable variant="filled" roundedControl
            expanded={this.state.isCustomerAgreementExpanded}
            onExpandedChange={(ev) => this.setState({ isCustomerAgreementExpanded: ev.detail })}>
          <span slot="title">Customer Agreement</span>
          <span slot="content">
            <CustomerAgreementDetails agreement={this.state.agreement} 
              refLists={this.state.refListsComplete} 
              onAccountSearchClick={(arg) => this.props.onAccountSearchClick(arg)} 
              onLocalIdSearchClick={(arg) => this.props.onLocalIdSearchClick(arg)}
            />
          </span>
        </OwcExpandable>
        <OwcExpandable
          expanded={this.state.conflictingAgreementsExpanded}
          onExpandedChange={(ev) => this.setState({ conflictingAgreementsExpanded: ev.detail })}
          roundedControl
          variant="transparent"
          style={{ borderTop: '2px solid gray' }}
        >
          <span slot="title">
              Conflicting Agreements - {this.state.agreementList.length} Conflicting
              agreement{this.state.agreementList.length !== 1 ? 's' : ''} selected
          </span>
          <span slot="content">
            <div style={{ padding: '0rem 3rem' }}>
              {this.renderAgreementFilterSection()}
            </div>
          </span>
        </OwcExpandable>

        {this.state.conflictingDevicesLoaded && (
          <OwcExpandable
            expanded={this.state.resultExpanded}
            onExpandedChange={(ev) => this.setState({ resultExpanded: ev.detail })}
            roundedControl
            variant="transparent"
            style={{ borderTop: '2px solid gray', marginTop: '1rem' }}
          >
            <span
              slot="title"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
            >
                {totalConflicts === 0
                  ? 'No Conflicting Devices Found'
                  : 'Conflicting Devices - ' + uniqueDeviceCount + ' unique device' + (uniqueDeviceCount !== 1 ? 's' : '') + ' found (' + totalConflicts + ' conflicts)'
                }
              {totalConflicts > 0 && (
                <OwcButton
                  style={{ marginRight: '3rem' }}
                  variant="primary"
                  onClick={(e) => { e.stopPropagation(); this.downloadConflictsCSV(); }}
                >
                  Download CSV
                </OwcButton>
              )}
            </span>
            <span slot="content">
              {totalConflicts === 0 ? (
                <div style={{ padding: '2rem 3rem', textAlign: 'center' }}>
                  <OwcTypography variant="subtitle1" style={{ color: 'gray' }}>
                    No conflicting devices were found between the selected agreements.
                  </OwcTypography>
                </div>
              ) : (
                <div style={{ padding: '0rem 3rem' }}>
                  {this.renderConflictingDevicesTable()}
                </div>
              )}
            </span>
          </OwcExpandable>
        )}
      </div>
    );
  }
}

export default identifyConflicts;
